import { useState } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export function ChangePasswordCard() {
  const { logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres"); return; }
    if (next !== confirm) { toast.error("Las contraseñas nuevas no coinciden"); return; }
    if (next === current) { toast.error("La nueva contraseña debe ser distinta a la actual"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión con la nueva.");
      setCurrent(""); setNext(""); setConfirm("");
      // Since backend invalidates the current JWT (password_changed_at is now newer than iat),
      // subsequent requests will 401. Log out cleanly.
      setTimeout(() => { logout && logout(); window.location.href = "/login"; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-1">
        <KeyRound size={18} className="text-[#EF4444]" />
        <h3 className="font-display text-xl">Cambiar contraseña</h3>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Al cambiarla te enviaremos un correo de aviso con la opción &quot;¿No fuiste tú?&quot; y cerraremos tus sesiones activas.
      </p>
      <form onSubmit={submit} className="space-y-3 max-w-md">
        <div>
          <Label className="text-neutral-400 mb-1 block text-xs uppercase tracking-wider">Contraseña actual</Label>
          <Input
            data-testid="cp-current"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            className="bg-[#171717] border-[#262626] text-white"
            autoComplete="current-password"
          />
        </div>
        <div>
          <Label className="text-neutral-400 mb-1 block text-xs uppercase tracking-wider">Nueva contraseña (mín 8)</Label>
          <Input
            data-testid="cp-new"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            minLength={8}
            required
            className="bg-[#171717] border-[#262626] text-white"
            autoComplete="new-password"
          />
        </div>
        <div>
          <Label className="text-neutral-400 mb-1 block text-xs uppercase tracking-wider">Confirmar nueva</Label>
          <Input
            data-testid="cp-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={8}
            required
            className="bg-[#171717] border-[#262626] text-white"
            autoComplete="new-password"
          />
        </div>
        <Button
          data-testid="cp-submit"
          type="submit"
          disabled={loading || !current || !next || !confirm}
          className="bg-[#EF4444] hover:bg-[#DC2626]"
        >
          <BusyLabel busy={loading} text="Actualizando…">Cambiar contraseña</BusyLabel>
        </Button>
      </form>
    </div>
  );
}
